import axios from 'axios';
import moment from 'moment';
import { FIVE_MINUTES, PAGING_DEFAULT } from '../constants/constants';
import { addParamsToQueryString } from '../functions/utils';
import { IApiResponse } from '../interfaces/IApiResponse';
import User from '../models/user';
import urls from './urls';
import { IParamsRequest } from '../interfaces/IAction';
import { RequestInviteParentType, CreateUserInvite } from '../types/invites';
import { InviteType } from '../enum/InviteEnum';
import { IInviteCredit } from '../models/consumable';

export const generateParams = (params: Record<string, string | number | any>): string => {
  const mappedParams = Object.entries(params).map(entry => `${entry[0]}=${entry[1]}`);
  return mappedParams.join('&');
};

class UserApi {
  static getFacebookUserInfo(token: string): Promise<IApiResponse> {
    const url = urls.FACEBOOK_ME_PATH + token;
    return axios.get(url, { baseURL: urls.FACEBOOK_URL, headers: { isAnonymous: false } });
  }

  static getAutoCompletePlace(place: string, key: string, queryParams: string): Promise<IApiResponse> {
    const url = urls.GOOGLE_AUTOCOMPLETE_PATH.replace('{inputValue}', place).replace('{keyValue}', key) + queryParams;
    return axios.get(url, { baseURL: urls.GOOGLE_URL, headers: { isAnonymous: true } });
  }

  static getDetailPlace(placeid: string, key: string): Promise<IApiResponse> {
    const url = urls.GOOGLE_PLACE_DETAILS_PATH.replace('{placeidValue}', placeid).replace('{keyValue}', key);
    return axios.get(url, { baseURL: urls.GOOGLE_URL, headers: { isAnonymous: true } });
  }

  static getQuickProfileByXmppId(xmppId: string): Promise<IApiResponse> {
    const url = urls.GET_QUICK_PROFILE.replace('{xmppId}', encodeURIComponent(xmppId));
    return axios.get(url, { baseURL: urls.BASE_URL, headers: { isAnonymous: false } });
  }

  static getMyQuickProfile(): Promise<IApiResponse> {
    const url = urls.GET_MY_QUICK_PROFILE;
    return axios.get(url, { baseURL: urls.BASE_URL, headers: { isAnonymous: false } });
  }

  static getUserXmppDetail(): Promise<IApiResponse> {
    const url = urls.GET_USER_XMPP_DETAIL;
    return axios.get(url, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }

  static getProfileSummary(profileId: number): Promise<IApiResponse> {
    const url = urls.GET_PROFILE_SUMMARY.replace('{profileId}', profileId.toString());
    return axios.get(url, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }

  static getUserMainPhoto(): Promise<IApiResponse> {
    const url = urls.GET_USER_MAIN_PHOTO;
    return axios.get(url, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }

  static getUserProfileSummary(buildVersion?: string): Promise<IApiResponse> {
    let url = urls.GET_USER_PROFILE;
    if (buildVersion) {
      url = `${url}?version=${buildVersion}`;
    }
    return axios.get(url, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }

  static addReport(report: { profileId: string; Id: string }): Promise<IApiResponse> {
    const url = urls.REPORT_USER.replace('{profileId}', report.profileId || report.Id);
    return axios.post(url, report, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }

  static getMainPhoto(profileId: number): Promise<IApiResponse> {
    const url = urls.GET_MAIN_PHOTO.replace('{profileId}', String(profileId));
    return axios.get(url, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }

  static getUsersProfile(
    params: { page?: number; pageSize?: number; SearchGuid?: string; GoogleMapPlace?: string } = {}
  ): Promise<IApiResponse> {
    const body: Record<string, any> = {
      Page: params.page || 1,
      Size: params.pageSize || PAGING_DEFAULT.PAGE_SIZE_MEMBER_SEARCH,
    };
    if (params.SearchGuid) {
      body.SearchGuid = params.SearchGuid;
    }
    const queryString =
      (params &&
        Object.keys(body)
          .map(k => `${encodeURIComponent(k)}=${encodeURIComponent(body[k])}`)
          .join('&')) ||
      null;
    const url =
      (params.GoogleMapPlace &&
        `${urls.GET_PROFILE_SEARCH}${queryString ? '?' : ''}${queryString}&GoogleMapPlace=${JSON.stringify(params.GoogleMapPlace).split(new RegExp(`"`, 'i')).join('%22')}`) ||
      `${urls.GET_PROFILE_SEARCH}${queryString ? '?' : ''}${queryString}`;
    return axios.get(url, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }

  static addFavorite(id: string): Promise<IApiResponse> {
    const url = urls.ADD_FAVORITE.replace('{userId}', id);
    return axios.post(url, null, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }

  static removeFavorite(params: any): Promise<IApiResponse> {
    const url = urls.DELETE_FAVORITE;
    return axios.patch(url, params, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }

  static getFriendsFavorites(params: { pageSize: number; pageNumber: number }, body: any): Promise<IApiResponse> {
    const url = urls.GET_FRIEND_AND_FAVORITE.replace('{pageSize}', params.pageSize.toString()).replace(
      '{pageNumber}',
      params.pageNumber.toString()
    );
    return axios.post(url, body, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }

  static blockUser(userId: string): Promise<IApiResponse> {
    const url = urls.BLOCK_USER.replace('{userId}', userId);
    return axios.post(url, null, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }

  static blockUserChat(userId: string): Promise<IApiResponse> {
    const url = urls.CHAT_BLOCK.replace('{userId}', userId);
    return axios.post(url, null, { baseURL: urls.BASE_URL, headers: { isAnonymous: false } });
  }

  static getMatchingUsers(): Promise<IApiResponse> {
    const url = urls.GET_LIST_MATCHING;
    return axios.get(url, { baseURL: urls.BASE_URL, headers: { isAnonymous: false } });
  }

  static addMatchingUser(params: any): Promise<IApiResponse> {
    const url = urls.ADD_MATCHING_USER;
    return axios.post(url, params, { baseURL: urls.BASE_URL, headers: { isAnonymous: false } });
  }

  static getUserSettings(): Promise<IApiResponse> {
    const url = `${urls.GET_USER_SETTINGS}?tz=${moment()}`;
    return axios.get(url, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }

  static patchUserClientSettings(params: any): Promise<IApiResponse> {
    const url = urls.PATH_USER_CLIENT_SETTINGS;
    return axios.patch(url, params, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }

  static patchUserMessengerSettings(params: any): Promise<IApiResponse> {
    const url = urls.PATCH_USER_MESSENGER_SETTINGS;
    return axios.patch(url, params, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }

  static patchUserChatRoomSettings(params: any): Promise<IApiResponse> {
    const url = urls.PATCH_USER_CHATROOM_SETTINGS;
    return axios.patch(url, params, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }

  static getMainPhotos(userIds: string): Promise<IApiResponse> {
    const url = urls.GET_MAIN_PHOTOS.replace('{UserIDs}', userIds);
    return axios.get(url, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }

  static getProfileSummaries(params: string): Promise<IApiResponse> {
    const url = urls.GET_MAIN_PHOTOS.replace('{Ids}', params);
    return axios.get(url, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }

  static getProfileSummariesDetails(params: string[]): Promise<IApiResponse> {
    const url = urls.GET_PROFILE_SUMMARIES.replace('{ids}', params.toString());
    return axios.get(url, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }

  static checkUserOnline(xmppId: string): Promise<IApiResponse> {
    const url = urls.CHECK_USER_ONLINE.replace('{xmppId}', encodeURIComponent(xmppId));
    return axios.get(url, { baseURL: urls.BASE_URL, headers: { isAnonymous: false } });
  }

  static patchUpdateAccountTooltips(id: string): Promise<IApiResponse> {
    const url = urls.UPDATE_ACCOUNT_TOOLTIPS.replace('{ID}', id);
    return axios.post(url, null, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }

  static getUserWhoBlockMeList(): Promise<IApiResponse> {
    const url = urls.GET_USERS_WHO_BLOCK_ME;
    return axios.get(url, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }

  static getUsersBlocked(): Promise<IApiResponse> {
    const url = urls.GET_USERS_BLOCKED_TIGASE;
    return axios.get(url, { baseURL: urls.BASE_URL, headers: { isAnonymous: false } });
  }

  static getUserStatus(userId: number | string | null): Promise<IApiResponse> {
    const url = urls.GET_USER_STATUS.replace('{userId}', userId ? userId.toString() : '');
    return axios.get(url, { baseURL: urls.BASE_URL, headers: { isAnonymous: false } });
  }

  static updateUserLocation(params: any): Promise<IApiResponse> {
    const url = urls.USER_ONLINE;
    return axios.post(url, params, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }

  static patchUpdateAccountSetting(params: any): Promise<IApiResponse> {
    const url = urls.PATCH_UPDATE_ACCOUNT_SETTING;
    return axios.patch(url, params, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }

  static createUserToolTip(toolTipId: number): Promise<IApiResponse> {
    const url = urls.POST_USER_TOOLTIP.replace('{id}', toolTipId.toString());
    return axios.post(url, null, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }

  static updateUserSummaryTigase(userId: string, params: any): Promise<IApiResponse> {
    const url = urls.PUT_USER_INFO.replace('{userId}', userId);
    return axios.put(url, params, { baseURL: urls.BASE_URL, headers: { isAnonymous: false } });
  }

  static checkIsThisUserMe(userId: number): Promise<IApiResponse> {
    const url = urls.CHECK_USER_BLOCK_ME.replace('{userId}', userId.toString());
    return axios.get(url, { baseURL: urls.BASE_URL, headers: { isAnonymous: false } });
  }

  static getUsersActive(params: any): Promise<IApiResponse> {
    const url = urls.GET_USER_ACTIVE;
    return axios.post(url, params, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }

  static getActiveUserStateCount(params: any): Promise<IApiResponse> {
    const url = addParamsToQueryString(urls.GET_ACTIVE_USER_STATE_COUNT, params);
    return axios.get(url, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }

  static createUserAppRating(params: any): Promise<IApiResponse> {
    const url = urls.CREATE_USER_APP_RATING;
    return axios.post(url, params, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }

  static getAboutRhp(): Promise<IApiResponse> {
    const url = urls.GET_ABOUT_RHP;
    return axios.get(url, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }

  static getSearchFriendsOnline(params: any): Promise<IApiResponse> {
    const url = addParamsToQueryString(urls.GET_SEARCH_FRIEND_ONLINE, params);
    return axios.get(url, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }

  static getSearchFavouriteOnline(params: any): Promise<IApiResponse> {
    const url = addParamsToQueryString(urls.GET_SEARCH_FAVORITE_ONLINE, params);
    return axios.get(url, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }

  static getUserSharedPhotos(params: any): Promise<IApiResponse> {
    const url = addParamsToQueryString(urls.GET_USER_SHARED_MEDIA, params);
    return axios.get(url, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }

  static patchUserSharedPhotos(params: any): Promise<IApiResponse> {
    const url = urls.GET_USER_SHARED_PHOTOS;
    return axios.patch(url, params, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }

  static deleteUserVideos(params: string): Promise<IApiResponse> {
    const url = `${urls.DELETE_USER_VIDEOS}?${params}`;
    return axios.delete(url, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }

  static getProfileVideo(ownerUserId: string, videoId: string): Promise<IApiResponse> {
    const url = urls.GET_PROFILE_VIDEO.replace('{Id}', ownerUserId).replace('{VideoID}', videoId);
    return axios.get(url, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }

  static getProfileAudio(ownerUserId: string, fileName: string): Promise<IApiResponse> {
    const url = urls.GET_PROFILE_AUDIO.replace('{Id}', ownerUserId).replace('{FileName}', fileName);
    return axios.get(url, {
      baseURL: urls.BASE_RHPURL,
      headers: { isAnonymous: false },
      responseType: 'arraybuffer',
      timeout: FIVE_MINUTES,
    });
  }

  static login(): Promise<IApiResponse> {
    const url = urls.USER_LOGIN;
    return axios.get<any, IApiResponse>(url, { baseURL: urls.BASE_URL, headers: { isAnonymous: true } }).then(response => {
      try {
        if (response.data.success) {
          response.data.data = User.mappingObject(response.data.data);
        }
        return response;
      } catch (e) {
        console.log('ERROR: userService/login:', e);
        throw new Error('failed');
      }
    });
  }

  static deleteParentOwnedComment(params: { commentId: string }): Promise<IApiResponse> {
    const url = urls.DELETE_PARENT_OWNED_COMMENT.replace('{Id}', params.commentId);
    return axios.delete(url, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }

  static getManualPhoneVerificationCode(): Promise<IApiResponse> {
    return axios.get(urls.GET_MANUAL_PHONE_VERIFICATION_CODE, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }

  static getActiveSubmissionCount(params: { userId: string }): Promise<IApiResponse> {
    const url = urls.GET_ACTIVE_SUBMISSION_COUNT.replace('{RecipientUserID}', params.userId);
    return axios.get(url, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }

  static getServerTime(): Promise<IApiResponse> {
    const url = urls.SERVER_TIME;
    return axios.get(url, { baseURL: urls.BASE_URL, headers: { isAnonymous: false } });
  }

  static getUserLivenessSessionId(): Promise<IApiResponse> {
    const url = urls.GET_USER_LIVENESS_SESSION_ID;
    return axios.get(url, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }

  static getUserLivenessResult(sessionId: string): Promise<IApiResponse> {
    const url = `${urls.GET_USER_LIVENESS_RESULT}?SessionID=${sessionId}`;
    return axios.get(url, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }

  static getUserProfileBoostStats(): Promise<IApiResponse> {
    const url = `${urls.GET_USER_PROFILE_BOOST_STATS}`;
    return axios.get(url, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }

  static createUserInvite({ recipientUserId, parentId, parentType }: CreateUserInvite): Promise<
    IApiResponse<{
      inviteResponse: InviteType;
      status: IInviteCredit;
    }>
  > {
    const url = urls.CREATE_USER_INVITE;
    const body = {
      RecipientUserID: recipientUserId,
      ParentID: parentId,
      ParentType: parentType,
    };
    return axios.post(url, body, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }

  static getReceivedInvites(params: IParamsRequest): Promise<IApiResponse> {
    const queryString = generateParams({
      Size: (params && params.pageSize) || PAGING_DEFAULT.PAGE_SIZE_MEMBER_SEARCH,
      Page: (params && params.page) || 1,
    });
    const url = `${urls.GET_RECEIVED_INVITES.replace('{parentType}', params.parentType)}?${queryString}`;
    return axios.get(url, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }

  static deleteReceivedInvites(inviteID: string): Promise<IApiResponse> {
    const url = urls.DELETE_RECEIVED_INVITES.replace('{inviteID}', inviteID);
    return axios.delete(url, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }

  static postAffiliateTraffic(params: { Affiliate: string; Referer: string }): Promise<IApiResponse> {
    const url = urls.POST_AFFILIATE_TRAFFIC;
    return axios.post(url, params, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: true } });
  }
}

export default UserApi;
